import { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  life: number;
  maxLife: number;
}

interface Node {
  x: number;
  y: number;
  radius: number;
  pulsePhase: number;
  connections: number[];
}

interface TechFlowVideoProps {
  mode?: 'background' | 'card' | 'stream';
  color?: 'accent' | 'secondary';
  density?: number;
  className?: string;
}

const COLOR_MAP = {
  accent: { r: 112, g: 87, b: 255 },
  secondary: { r: 34, g: 211, b: 197 },
};

export default function TechFlowVideo({
  mode = 'card',
  color = 'accent',
  density = 1,
  className = '',
}: TechFlowVideoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const nodesRef = useRef<Node[]>([]);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);

  const rgb = COLOR_MAP[color];

  const initParticles = useCallback(
    (width: number, height: number) => {
      const count = Math.floor((mode === 'background' ? 60 : mode === 'stream' ? 120 : 80) * density);
      const arr: Particle[] = [];
      for (let i = 0; i < count; i++) {
        arr.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * (mode === 'stream' ? 2.5 : 1.2),
          vy: (Math.random() - 0.5) * (mode === 'stream' ? 2.5 : 1.2),
          size: Math.random() * (mode === 'background' ? 1.5 : 2.5) + 0.5,
          opacity: Math.random() * 0.6 + 0.2,
          life: Math.random() * 300,
          maxLife: 200 + Math.random() * 400,
        });
      }
      particlesRef.current = arr;
    },
    [mode, density]
  );

  const initNodes = useCallback(
    (width: number, height: number) => {
      const count = Math.floor((mode === 'background' ? 8 : 12) * density);
      const arr: Node[] = [];
      for (let i = 0; i < count; i++) {
        const connections: number[] = [];
        const numConnections = 1 + Math.floor(Math.random() * 3);
        for (let j = 0; j < numConnections; j++) {
          const target = Math.floor(Math.random() * count);
          if (target !== i && !connections.includes(target)) {
            connections.push(target);
          }
        }
        arr.push({
          x: Math.random() * width,
          y: Math.random() * height,
          radius: 2 + Math.random() * 3,
          pulsePhase: Math.random() * Math.PI * 2,
          connections,
        });
      }
      nodesRef.current = arr;
    },
    [mode, density]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;
        initParticles(canvas.width, canvas.height);
        initNodes(canvas.width, canvas.height);
      }
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const handleMouseLeave = () => {
      mouseRef.current = null;
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.016;

      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      // Update & draw particles
      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.life += 1;

        // Mouse attraction
        if (mouse && mode !== 'background') {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            const force = (1 - dist / 200) * 0.02;
            p.vx += dx * force;
            p.vy += dy * force;
          }
        }

        // Fade in/out based on life
        const lifeRatio = p.life / p.maxLife;
        let alpha = p.opacity;
        if (lifeRatio < 0.1) alpha *= lifeRatio / 0.1;
        else if (lifeRatio > 0.85) alpha *= (1 - lifeRatio) / 0.15;

        // Speed dampening
        p.vx *= 0.999;
        p.vy *= 0.999;

        // Wrap around
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;

        // Reset dead particles
        if (p.life >= p.maxLife) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
          p.vx = (Math.random() - 0.5) * (mode === 'stream' ? 2.5 : 1.2);
          p.vy = (Math.random() - 0.5) * (mode === 'stream' ? 2.5 : 1.2);
          p.life = 0;
          p.maxLife = 200 + Math.random() * 400;
        }

        // Glow effect
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
        gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`);
        gradient.addColorStop(0.3, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.6})`);
        gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Update & draw nodes
      const nodes = nodesRef.current;
      nodes.forEach((node, i) => {
        node.pulsePhase += 0.02;

        // Gentle drift
        node.x += Math.sin(time * 0.5 + i) * 0.15;
        node.y += Math.cos(time * 0.7 + i) * 0.15;

        // Keep in bounds
        if (node.x < 20) node.x = 20;
        if (node.x > w - 20) node.x = w - 20;
        if (node.y < 20) node.y = 20;
        if (node.y > h - 20) node.y = h - 20;

        const pulse = 1 + Math.sin(node.pulsePhase) * 0.4;

        // Draw connections first (behind nodes)
        node.connections.forEach((targetIdx) => {
          const target = nodes[targetIdx];
          if (!target) return;
          const dx = target.x - node.x;
          const dy = target.y - node.y;

          // Flowing data dots along connection
          const dotCount = 3;
          for (let d = 0; d < dotCount; d++) {
            const progress = ((time * 0.8 + d / dotCount) % 1);
            const dotX = node.x + dx * progress;
            const dotY = node.y + dy * progress;
            ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.25 * pulse})`;
            ctx.beginPath();
            ctx.arc(dotX, dotY, 1.2, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(target.x, target.y);
          ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.08 * pulse})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        });

        // Node glow
        const glowRadius = node.radius * 3 * pulse;
        const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius);
        glow.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.5 * pulse})`);
        glow.addColorStop(0.4, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.2 * pulse})`);
        glow.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();

        // Node core
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.7 * pulse})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
        ctx.fill();

        // Outer ring
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.3 * pulse})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 2 * pulse, 0, Math.PI * 2);
        ctx.stroke();
      });

      // Data stream lines (horizontal flowing streaks) — only in stream/background modes
      if (mode !== 'card') {
        const streamCount = mode === 'stream' ? 6 : 3;
        for (let s = 0; s < streamCount; s++) {
          const baseY = (h / (streamCount + 1)) * (s + 1) + Math.sin(time * 0.3 + s) * 30;
          const progress = (time * 0.4 + s * 0.3) % 1;

          // Draw dashed stream line
          ctx.setLineDash([6, 20]);
          ctx.lineDashOffset = -progress * 26;
          ctx.beginPath();
          ctx.moveTo(0, baseY);
          ctx.lineTo(w, baseY);
          ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06)`;
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);

          // Glowing dots along streams
          for (let d = 0; d < 5; d++) {
            const dotProgress = ((time * 0.6 + d / 5 + s * 0.2) % 1);
            const dotX = dotProgress * w;
            const dotY = baseY + Math.sin(dotProgress * 10) * 4;
            const dotAlpha = Math.sin(dotProgress * Math.PI) * 0.3;

            const dotGlow = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, 8);
            dotGlow.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${dotAlpha})`);
            dotGlow.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
            ctx.fillStyle = dotGlow;
            ctx.beginPath();
            ctx.arc(dotX, dotY, 8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animRef.current);
    };
  }, [mode, color, density, rgb, initParticles, initNodes]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
    />
  );
}